import { effect, inject, InjectionToken, untracked } from '@angular/core'
import { patchState, signalStore, withFeature, withHooks, withMethods, withState } from '@ngrx/signals'
import { Events, withEventHandlers } from '@ngrx/signals/events'
import { filter, map, tap } from 'rxjs'

import { isDefined } from '@app/core/is-defined'
import { ConversationStore } from '@app/modules/messaging/components/store/conversation.store'
import { voiceToSendMessagesPayload } from '@app/modules/messaging/components/store/sending.feature.utils'
import type { Conversation } from '@app/modules/messaging/models/conversation.model'
import type { MessageForm } from '@app/modules/messaging/models/message-form.model'
import { recordingFeature } from '@app/modules/recording/recording.feature'
import { S3UploadStrategy } from '@app/modules/s3-upload/core/s3-upload-strategy'
import { PopoverService } from '@app/overlays/popover/popover.service'
import { MemberService } from '@app/services/member.service'
import type { EventSpecificTrackingProperties } from '@app/services/track.service'
import { TrackService } from '@app/services/track.service'

import type { UnsentVoiceMessage } from '../../models/voice-message.model'
import { voiceUploadEvents } from '../../store/voice-upload.events'

export interface VoiceMessageRecorderConfig {
  messageForm: MessageForm
  conversation: Conversation
  minDurationMs: number
  maxDurationMs: number
}

export const VOICE_MESSAGE_RECORDER_CONFIG = new InjectionToken<VoiceMessageRecorderConfig>(
  'VOICE_MESSAGE_CREATOR_CONFIG',
)

interface State {
  boosted: boolean
}

const getInitialState = (): State => ({
  boosted: false,
})

export const VoiceMessageRecorderStore = signalStore(
  withState(getInitialState),
  withFeature((_state, config = inject(VOICE_MESSAGE_RECORDER_CONFIG)) =>
    recordingFeature('voice_message', config.minDurationMs, config.maxDurationMs),
  ),
  withMethods(
    (
      store,
      config = inject(VOICE_MESSAGE_RECORDER_CONFIG),
      popoverService = inject(PopoverService),
      trackService = inject(TrackService),
      memberService = inject(MemberService),
      conversationStore = inject(ConversationStore),
      s3UploadStrategy = inject(S3UploadStrategy),
    ) => {
      const toggleBoost = () => patchState(store, state => ({ boosted: !state.boosted }))

      const attemptToSendMessage = (voice: UnsentVoiceMessage, boosted: boolean) => {
        const { payload, fakeMessage } = voiceToSendMessagesPayload(
          voice,
          config.messageForm,
          boosted,
          memberService.account.voice_messages_expiration_hours,
        )

        conversationStore.attemptToSendMessage(config.conversation, payload, [fakeMessage])
      }

      const deleteVoice = () => {
        trackService.track('voice_message', {
          action: 'cancel',
          reason: 'delete',
          flow: conversationStore.voiceFlow()!,
          source: conversationStore.source,
        })

        store.clearMedia()
        patchState(store, { boosted: false })
        conversationStore.removeVoice()
        conversationStore.removeCache(config.conversation.profile)
        popoverService.show('nc-trash-can', 'Deleted')
      }

      const exitConfirmationModal = (reason: 'exit' | 'cancel') => {
        trackService.track('voice_message', {
          action: 'cancel',
          reason,
          flow: conversationStore.voiceFlow()!,
          source: conversationStore.source,
        })

        store.clearMedia()
        conversationStore.exitVoice()
        conversationStore.removeCache(config.conversation.profile)
      }

      const handleBlob = (file: File) => {
        const flow = conversationStore.voiceFlow()!
        const additionalProperties: EventSpecificTrackingProperties = {
          flow,
          source: conversationStore.source,
        }
        const batchUpload = s3UploadStrategy.batchFactory(
          [file],
          'voice_message',
          'media_recorder',
          additionalProperties,
        )
        conversationStore.handleVoiceUpload({
          batchUpload,
          conversation: config.conversation,
          flow,
        })
      }

      return { toggleBoost, attemptToSendMessage, deleteVoice, exitConfirmationModal, handleBlob }
    },
  ),
  withEventHandlers((store, events = inject(Events)) => ({
    errorOnUploadComplete$: events.on(voiceUploadEvents.uploadComplete).pipe(
      map(({ payload }) => payload.batchUpload.getErrorSummary()),
      filter(isDefined),
      tap(() => store.clearMedia()),
    ),
  })),
  withHooks({
    onInit(store) {
      effect(() => {
        const media = store.media()

        if (!media) {
          return
        }

        untracked(() => {
          const file = new File([media.blob], '', { type: media.blob.type })
          store.handleBlob(file)
        })
      })
    },
  }),
)
